import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import Feature from 'ol/Feature';
import { Point } from 'ol/geom';
import GeoJSON from 'ol/format/GeoJSON';
import Style from 'ol/style/Style';
import Stroke from 'ol/style/Stroke';
import { containsCoordinate } from 'ol/extent';
import Fill from 'ol/style/Fill';

import { throttle } from 'cadenza/utils/throttle';
import { unByKey } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';
import { determineIsochroneZoneColorOutsideIn } from 'cadenza/map/routing/isochrone-color-palette';
import { fitMapView } from 'cadenza/map/map-utils';
import { currentWindowMediaXsOnly } from 'cadenza/utils/media';

import { RoutingEvent, RoutingFeatureProperty, RoutingFeatureType } from '../routing-constants';

import {
  createErrorPointFeatureStyle,
  createPointFeatureStyle,
  createSelectedPointFeatureStyle
} from './waypoint-feature-style';

/* eslint-disable jsdoc/require-property-description */

/**
 * Type for the waypoint data objects that are used to pass waypoints around.
 *
 * @typedef {object} WaypointDto
 * @property {string} waypointId
 * @property {string} label
 * @property {number[]} coordinate
 * @property {string} locationHint
 * @property {boolean} selected
 * @property {boolean} invalid
 */

const DEFAULT_ROUTE_COLOR = '#cc0000';

export class RoutingFeatureLayer {

  #map;
  #mainLayer;
  #isochroneLayers = [];
  #eventBus;
  #eventKeys = [];

  /**
   * @type {WaypointDto[]}
   */
  #waypoints = [];

  #route;
  #lastAddedWaypointId;

  constructor (map, eventBus) {
    this.#map = map;
    this.#mainLayer = createFeatureLayer({ map, title: 'MainRoutingLayer' });
    this.#eventBus = eventBus;
    this.#eventBus.on(RoutingEvent.ROUTING_STARTED, () => this._onRoutingActivated());
  }

  _onRoutingActivated () {
    this.#mainLayer.setMap(this.#map);
    this.#eventKeys = [
      this.#eventBus.on(RoutingEvent.ROUTING_STOPPED, () => this._teardown()),
      this.#eventBus.on(RoutingEvent.WAYPOINTS_CHANGE, event => this._updateWaypoints(event.detail)),
      this.#eventBus.on(RoutingEvent.ROUTE_CHANGED, event => this._updateRoute(event.detail.route)),
      this.#eventBus.on(RoutingEvent.ROUTE_RESET, () => this._resetRoute()),
      this.#eventBus.on(RoutingEvent.ISOCHRONE_RESULT_CHANGED, event => this._updateIsochrones(event))
    ];
  }

  _teardown () {
    unByKey(this.#eventKeys);
    this.#eventKeys = [];
    this.#mainLayer.setMap(null);
    this._resetRoute();
  }

  get layer () {
    return this.#mainLayer;
  }

  get source () {
    return this.#mainLayer.getSource();
  }

  _updateWaypoints ({ waypoints, addedWaypointId }) {
    this.#waypoints = waypoints;
    if (addedWaypointId) {
      this.#lastAddedWaypointId = addedWaypointId;
    }
    this._updateFeatures();
  }

  _updateRoute (route) {
    this.#route = route;
    this._updateFeatures(true);
  }

  // This is throttled in order not to overload the CPU when dragging points
  _updateFeatures = throttle((fitMapToRoute = false) => {
    this.source.clear(true);

    this.#waypoints.forEach(waypoint =>
      this.#mainLayer.getSource().addFeature(createRoutingPointFeature(waypoint))
    );

    if (this.#route) {
      this.source.addFeature(
        createRouteFeature(this.#route.geometry)
      );
    }

    if (this.#lastAddedWaypointId) {
      this._fitMapZoomToIncludeNewPoint();
      this.#lastAddedWaypointId = null;
    }

    if (this.#route && fitMapToRoute) {
      this._fitMapToFeatures();
    }
  }, 50);

  _resetRoute () {
    this.source.clear(true);
    this.#route = null;
    this.#waypoints = [];
    this._clearIsochronesLayers();
  }

  /**
   * When a point's coordinates are added using the current location (GPS), it might be outside the
   * current map view. In this case we need to adjust the zoom level to include the point.
   *
   * @private
   */
  _fitMapZoomToIncludeNewPoint () {
    const addedWaypoint = this.#waypoints.find(it => it.waypointId === this.#lastAddedWaypointId);
    if (addedWaypoint) {
      const currentViewExtent = this.#map.getView().calculateVisibleExtent();
      const newWaypointIsOutsideCurrentViewExtent = !containsCoordinate(currentViewExtent, addedWaypoint.coordinate);
      if (newWaypointIsOutsideCurrentViewExtent) {
        this._fitMapToFeatures();
      }
    }
  }

  _fitMapToFeatures () {
    const featuresExtent = this.#mainLayer.getSource().getExtent();
    this._fitMapToExtent(featuresExtent);
  }

  _fitMapToExtent (extent) {
    const extentPadding = calculateExtentPadding(this.#map); // calculated in constructor gives incorrect result
    fitMapView(this.#map, extent, {
      padding: extentPadding,
      nearest: true
    });
  }

  _updateIsochrones (event) {
    this._clearIsochronesLayers();

    const isochronesResult = event.detail.isochroneResult;

    if (!isochronesResult) {
      return;
    }

    isochronesResult.isochrones.forEach((isochrone, isochroneIndex) => {
      this.#isochroneLayers.push(createIsochroneLayer(this.#map, isochrone, isochroneIndex));
    });

    this._fitMapToExtent(isochronesResult.extent);
  }

  _clearIsochronesLayers () {
    this.#isochroneLayers.forEach(layer => layer.setMap(null));
  }

}

function createFeatureLayer ({ map, title, opacity = 1 }) {
  const layer = new VectorLayer({
    title,
    source: new VectorSource(),
    opacity
  });
  layer.setMap(map);
  return layer;
}

function createRoutingPointFeature ({ coordinate, label, waypointId, selected, invalid }) {
  const feature = new Feature({
    geometry: new Point(coordinate),
    name: label
  });
  feature.setId(waypointId);
  feature.set(RoutingFeatureProperty.TYPE, RoutingFeatureType.WAYPOINT);
  feature.setStyle(createWaypointFeatureStyle(label, selected, invalid));
  return feature;
}

function createWaypointFeatureStyle (label, selected, invalid) {
  if (invalid) {
    return createErrorPointFeatureStyle(label);
  } else if (selected) {
    return createSelectedPointFeatureStyle(label);
  }
  return createPointFeatureStyle(label);
}

function createRouteFeature (geojsonGeometry) {
  const feature = new GeoJSON().readFeature(geojsonGeometry);
  feature.setId(uniqueId());
  feature.set(RoutingFeatureProperty.TYPE, RoutingFeatureType.ROUTE);
  feature.setStyle(createRouteFeatureStyle());
  return feature;
}

function createRouteFeatureStyle () {
  return new Style({
    stroke: new Stroke({
      color: DEFAULT_ROUTE_COLOR,
      width: 4,
      lineJoin: 'round'
    })
  });
}

function createIsochroneLayer (map, isochrone, isochroneIndex) {
  const totalNumberOfZones = isochrone.zones.length;
  const layer = createFeatureLayer({ map, title: `RoutingIsochrones-${isochroneIndex}`, opacity: 0.5 });
  isochrone.zones
    .map(zone => zone.zoneArea)
    .reverse() // we add the zones from larger to smaller
    .forEach((zoneGeometry, zoneIndex) => {
      layer.getSource().addFeature(
        createIsochroneZoneFeature(zoneGeometry, zoneIndex, totalNumberOfZones)
      );
    });
  return layer;
}

function createIsochroneZoneFeature (geojsonGeometry, zoneIndex, totalNumberOfZones) {
  const feature = new GeoJSON().readFeature(geojsonGeometry);
  feature.setId(uniqueId());
  feature.setStyle(createIsochroneZoneFeatureStyle(zoneIndex, totalNumberOfZones));
  return feature;
}

function createIsochroneZoneFeatureStyle (zoneIndex, totalNumberOfZones) {
  return new Style({
    fill: new Fill({
      color: determineIsochroneZoneColorOutsideIn(zoneIndex, totalNumberOfZones)
    })
  });
}

/**
 * To avoid put markers close to the edges, there is padding calculating the
 * extent in the map. The values are smaller for mobile because the devices are more limited.
 * To take care of the size of the top marker (avoiding to show it cut to the final user)
 * the top padding is more significant.
 *
 * @param {object} map - map
 * @return {number[]} The padding to be applied
 */
function calculateExtentPadding (map) {
  const extentPaddingDesktop = 100;
  const extentPaddingTopMobile = 50;
  const extentPaddingMobile = 10;

  return currentWindowMediaXsOnly.matches
    ? [ extentPaddingTopMobile, extentPaddingMobile, extentPaddingMobile, extentPaddingMobile ]
    : [ extentPaddingDesktop, extentPaddingDesktop, extentPaddingDesktop, extentPaddingDesktop ];
}
